import { supabase } from './supabase'

/**
 * Role administration (ACC-6).
 *
 * ── WHY THE ASSIGNABLE ROLES ARE A LIST HERE AND NOT A QUERY ────────────────
 *
 * `role` is readable, so the panel could list whatever the database holds. It
 * deliberately does not, for two reasons.
 *
 * The first is that not every role is a thing anyone should be handing out from
 * a web form. `tenant_admin` is on this list because somebody has to be able to
 * appoint a second one — the last-admin guard depends on it — but it is marked
 * as the one that grants the power to grant.
 *
 * The second is the descriptions. A role key tells an admin nothing:
 * "comms_admin" does not say that it carries the power to aim a message at one
 * business unit, and "moderator" does not say that it does not. Somebody
 * choosing who moderates ~900 colleagues' speech should be reading a sentence,
 * not a snake_case identifier. Those sentences have to be written by hand, and
 * writing them here keeps them next to the list they describe.
 *
 * `content_creator` was on this list and is gone — migration 022. It was
 * comms_admin under a second name: the only permission it carried that a
 * comms_admin did not was content.translate. Two entries describing one set of
 * powers is how an admin ends up granting the broader one by accident.
 *
 * `editor`, `approver` and `contributor` are gone too — migration 025. Same
 * shape, three more times: all three had zero holders, and two of them had
 * never been discussed in any session. `contributor` was the worst of them,
 * because it held neither content.create nor content.post and so could not
 * actually contribute anything; granting it would have been a role that did
 * nothing, silently.
 *
 * `employee` is absent on purpose: migration 021 grants it by trigger on tenant
 * membership and refuses to revoke it directly, so offering it would be a
 * control that does nothing.
 *
 * ── 140: EVERY GRANT, NOT FOUR OF THEM (9 Oct, David) ───────────────────────
 *
 * Until 140 this list held four roles and the panel looped over it, so every
 * other grant was DROPPED from the person's panel although people_roster
 * returned it: the tenant administrator's own panel hid five of his roles, and
 * a store manager's showed nothing at all. Star One admin, Stock Administrator
 * and the three support lanes had no screen anywhere — 064, 085 and 099 wrote
 * them straight into role_assignment and promised "the admin screen" for every
 * later grant. David's ruling: any admin.roles holder grants them, which is
 * what assign_role already allowed.
 *
 * So GRANT_CATALOGUE is every kind of grant a person can hold, grouped, and
 * the panel renders from it. Two rules keep it honest:
 *
 *   * A grant whose key is NOT here is still rendered (PersonPanel's "Other
 *     grants"), never dropped. Dropping by iteration was the bug.
 *   * `store_manager` is a CATALOGUE key, not a role. 064 made running a store
 *     a list (stock_room_manager), and 140 keeps it one: a store is a place,
 *     and has_permission's scope walks up the ORG TREE. people_roster carries
 *     it as a grant with scope_type 'stock_room'; it is changed through
 *     add/remove_stock_room_manager, never assign_role, which refuses it.
 */
export type RoleGroup = 'Administration' | 'Content' | 'Star One and store' | 'Support lanes'

/** The order the panel shows the groups in. */
export const ROLE_GROUPS: readonly RoleGroup[] = ['Administration', 'Content', 'Star One and store', 'Support lanes']

/** 140. The key people_roster uses for a store-manager grant. NOT a role. */
export const STORE_MANAGER_KEY = 'store_manager'

export type AssignableRole = {
  key: string
  name: string
  what: string
  group: RoleGroup
  /**
   * 140. 'role' is a row in role_assignment, changed by assign_role /
   * revoke_role. 'store' is the per-store manager list, changed by
   * add_stock_room_manager / remove_stock_room_manager, and scoped to a STORE
   * rather than an org unit. Absent means 'role'.
   */
  kind?: 'role' | 'store'
  /**
   * Whether this role may be granted for one part of the company rather than
   * all of it. Mirrors role.is_scopable — assign_role refuses a scope on a role
   * that is not scopable, so offering the picker here would produce an error
   * rather than a grant.
   */
  scopable?: boolean
  /**
   * 153. The kinds of org unit a scoped grant may name. Absent means any.
   * hr_admin's region is legal entities and business units (assign_role
   * refuses a department), so its picker offers only those.
   */
  scopeTypes?: readonly string[]
  /** Shown as a warning rather than a description. */
  caution?: string
  /**
   * 137. One of the two roles that administer PEOPLE. Granting or removing it
   * — and changing anything about somebody who holds it — needs
   * admin.roles.privileged, which only the tenant administrator holds.
   */
  privileged?: boolean
}

export const GRANT_CATALOGUE: AssignableRole[] = [
  {
    key: 'moderator',
    group: 'Content',
    name: 'Content Moderator',
    what:
      'Sees the review queue and decides what happens to a post the check flagged: leave it up, or take it down. Cannot publish or target anything.',
    caution:
      'This is a judgement about a colleague’s speech. Two people are named for launch; adding more is a decision, not a convenience.',
  },
  // 152: the Gallery is three roles, split by what each does with a
  // colleague's photo (rule 6). Keys never change, only labels: photo_room
  // (145) is the document user, and lost photo.location to gallery_admin.
  {
    key: 'gallery_browser',
    group: 'Content',
    name: 'Gallery browser',
    what:
      'Scrolls the Gallery’s photos at feed size and searches their captions. Sees what the feed already shows — the caption, who posted it and a link to the post — and can ask for any photo to be reviewed. Cannot download. Nothing is recorded.',
    caution:
      'A pilot role. When browsing is opened to every employee (one switch on the Gallery page), everybody has this and the grant stops mattering.',
  },
  {
    key: 'photo_room',
    group: 'Content',
    name: 'Gallery document user',
    what:
      'For people who make training, sales and marketing material. Opens the Gallery with search, filters and the detail panel: tags, who is in the photo, its description, the place name (country, region, town), the uses allowed and whether a review is open. Adds tags and accepts or rejects OneConetec’s suggestions. Downloads the feed-size copy (2000 px) for a stated use.',
    caution:
      'Every download is recorded with this person’s name and the Gallery admins can see it. A use the poster opted out of cannot be downloaded, and nothing downloads while a photo is under review.',
  },
  {
    key: 'gallery_admin',
    group: 'Content',
    name: 'Gallery admin',
    what:
      'Looks after the library: everything a document user sees, plus the camera, date taken and exact location, the full-size original, who downloaded what, why a photo was opted out, and review requests with their reasons. Edits the tag list, accepts site names, and decides photo reviews or hands one to a named colleague.',
    caution:
      'Sees colleagues’ GPS and their download activity, and the names of people who ask for reviews. Keep it small — the photo library owners.',
  },
  // 146 (CMP-4); 147 (CMP-6) relabelled it. The KEY stays photo_contest_judge
  // (rule 7), but judging is now being NAMED on a contest; this role ORGANISES
  // contests. It still carries photo.room.
  {
    key: 'photo_contest_judge',
    group: 'Content',
    name: 'Contest organiser',
    what:
      'Runs photo contests: creates popular-vote and judged contests and the standing monthly contest, sets their words, dates, places, stars and judges, and can cancel one. Also opens the Gallery as a document user, because judging needs the full search.',
    caution:
      'The judges named on a contest confirm its results, which is final and pays each winner their place’s stars (up to 10,000 a place). Holding this role does not make anyone a judge.',
  },
  // 310 made it; 317 puts it on this panel (David, 25 Oct: "is that role
  // available on admin/people?"). Until then it showed only under Other grants.
  {
    key: 'survey_manager',
    group: 'Content',
    name: 'Survey manager',
    what:
      'Makes surveys on the Surveys page: writes the questions, chooses who is asked (everyone, part of the company, named people, a role, or anyone with the link), opens and closes them, shows the answers live on a screen with a QR code, and archives or deletes them. Sees only the surveys they run or that are shared with them — never anyone else’s.',
    caution:
      'A survey that records names shows each answer with the person who gave it, to whoever runs it and anyone they share the results with. Co-owners and results viewers are chosen per survey and do not need this role.',
  },
  {
    key: 'comms_admin',
    group: 'Content',
    name: 'Comms Administrator',
    what:
      'The editorial role. Writes, translates, publishes, and can aim a post at one business unit rather than the whole company. Promotes to the hero and the banner, pins, curates Wiki One answers, and reads aggregate reach. Does not moderate.',
    scopable: true,
    caution:
      'Targeting is editorial power — this is who decides what a specific crew sees first. Given for one part of the company, it bounds targeting to that part and its business units, and removes promotion entirely: the hero is one slot for the whole tenant and cannot be shared.',
  },
  {
    key: 'hr_admin',
    group: 'Administration',
    name: 'HR Administrator',
    what:
      'Runs People and roles: invites people, edits where they sit, what they do and how they are reached, suspends and restores access, and grants every role except these last two. Does not change the org structure or the BambooHR mapping. Given for the whole company (group HR) or for a region: one or more legal entities, or business units, each granted separately.',
    caution:
      'A regional grant sees and changes only the people whose legal entity or business unit is in the region, and gives them any role but these two — Comms Administrator only for a part of the region. It cannot invite someone new, move a person out of the region, or change anything company-wide. Either way, it cannot appoint or remove a Tenant or HR Administrator, or change anything about someone who is one — including themselves.',
    privileged: true,
    // 153: role.is_scopable is true. A region is one grant row per unit.
    scopable: true,
    scopeTypes: ['legal_entity', 'business_unit'],
  },
  {
    key: 'tenant_admin',
    group: 'Administration',
    name: 'Tenant Administrator',
    what: 'Manages the org structure and who holds which role, including who the HR administrators are.',
    caution:
      'Grants the power to grant. The last one cannot be removed or suspended — appoint a second before removing the first.',
    privileged: true,
  },
  // ── 140: the five that had no screen, and the store list ────────────────
  {
    key: 'stars_admin',
    group: 'Star One and store',
    name: 'Star One admin',
    what:
      'Sets how many stars each activity earns and its monthly cap, curates endorsements (official, translated, featured, hidden, merged), and reads aggregate star totals per activity. Not the store, not roles, and never a per-person list.',
    caution: 'Changes what every colleague earns. A new rate applies to everyone from the moment it is saved.',
  },
  {
    key: 'stock_admin',
    group: 'Star One and store',
    name: 'Stock Administrator',
    what:
      'Runs the store as a whole: creates, edits and closes stores, appoints store managers, adds items priced for the whole company, and edits any item — price, variants, pictures, who may order it. Does everything a store manager does, in every store.',
    caution:
      'Sets star prices for the whole company and decides who runs each store. Manages every store without being listed on any of them.',
  },
  {
    key: STORE_MANAGER_KEY,
    group: 'Star One and store',
    kind: 'store',
    name: 'Store manager',
    what:
      'Runs one store: counts stock, sets reorder levels and backorders, moves stock between the stores they run, works the picking list, and collects or cancels orders. Adds and prices items that belong to that store only. Given per store — a person can run several, and each is removed separately.',
    caution: 'Sees the full name of everyone who orders from that store.',
  },
  {
    key: 'support.platform',
    group: 'Support lanes',
    name: 'Platform support',
    what:
      'Answers the Platform support lane in Ask One: the hub itself — something broken, confusing or missing, and where ideas go. Can send a ticket on to the developers. Carries no other power.',

    caution:
      'Reads every question in the Platform lane, private ones included, and decides which become tickets for the developers.',
  },
  {
    key: 'support.hr',
    group: 'Support lanes',
    name: 'HR support',
    what: 'Answers the HR lane in Ask One: pay, leave, contracts and benefits. Carries no other power.',
    caution:
      'Reads colleagues’ private questions to HR. Employees are offered the lane only while somebody holds it.',
  },
  {
    key: 'support.technical',
    group: 'Support lanes',
    name: 'Technical expert',
    what:
      'Answers the Technical expert lane in Ask One: field data, testing, equipment, client deliverables — the work, not the hub. Carries no other power.',

    caution:
      'Reads every private question in the Technical lane, and the lane goes straight to a person — whoever holds it must know the work.',
  },
]

/**
 * The roles proper — every catalogue entry that assign_role accepts. What the
 * bulk grant offers, and what may be sent to assign_role / revoke_role.
 */
export const ASSIGNABLE_ROLES: AssignableRole[] = GRANT_CATALOGUE.filter((r) => (r.kind ?? 'role') === 'role')

/** 140. The catalogue entry for a key, or undefined for one this file has never heard of. */
export function catalogueEntry(key: string): AssignableRole | undefined {
  return GRANT_CATALOGUE.find((r) => r.key === key)
}

/** 137. The roles that administer people. See `privileged` above. */
export const PRIVILEGED_ROLE_KEYS: readonly string[] = ASSIGNABLE_ROLES.filter((r) => r.privileged).map(
  (r) => r.key,
)

/**
 * Whether this person holds a role that only a tenant administrator may touch.
 * Presentation only: app.require_privileged_for (137) is the gate, and it
 * reads role_assignment, which is what `roleKeys` was read from.
 */
export function holdsPrivilegedRole(p: Pick<PersonRoles, 'roleKeys'>): boolean {
  return p.roleKeys.some((k) => PRIVILEGED_ROLE_KEYS.includes(k))
}

/**
 * One grant. There may be several of the same role for one person — that is
 * what scope means, and it is why `roleKeys` alone cannot describe somebody.
 */
export type RoleGrant = {
  key: string
  name: string
  /**
   * 140. What `scopeId` points at: an org unit (a scoped role, 024) or a store
   * (a store-manager grant). null for a tenant-wide grant.
   */
  scopeType?: 'org_unit' | 'stock_room' | null
  scopeId: string | null
  scopeName: string | null
  /** 140. For a store: false when the store is closed. Absent otherwise. */
  scopeActive?: boolean
}

/** Where somebody sits. Plural because a person may be in more than one unit. */
export type OrgPlacement = {
  orgUnitId: string
  name: string
  type: string
  parentId: string | null
  parentName: string | null
}

/**
 * The four states the Access cell shows. Computed in the DATABASE (044) and
 * rendered as-is — do not re-derive the ladder here. The filter compares
 * against this same string, so a row can never be labelled Active and then
 * hidden by the Active filter.
 *
 * 'unknown' is the 042 case: the caller may not see this person's sign-in
 * state. Unreachable through this function since 043 made it admin-only, and
 * kept because that is exactly the guard that stops it silently becoming
 * 'never_signed_in' again.
 */
export type AccessStatus =
  | 'active'
  | 'suspended'
  | 'not_invited'
  | 'invited'
  | 'unverified'
  | 'unknown'

export type PersonRoles = {
  personId: string
  displayName: string
  locale: string
  isActive: boolean
  /** Whether their tenant membership is live. This is what suspension changes. */
  membershipActive: boolean
  /** null means "you may not see this", which is not the same as false. */
  isVerified: boolean | null
  /** null means "you may not see this", which is not the same as false. */
  hasSignedIn: boolean | null
  /**
   * When they last SIGNED IN — not when they were last active. See the header
   * and migration 044. null means either never, or not permitted; the pair
   * with `hasSignedIn` is what tells those apart.
   */
  lastSignInAt: string | null
  /** When the identity row was created — 010 makes that row the invitation. */
  invitedAt: string | null
  /** Last time an admin triggered a code. Null does not mean never invited. */
  lastInviteSentAt: string | null
  /**
   * Whether an external system (BambooHR) is answerable for this person.
   * False for anyone added by hand, and the auto-suspend must never touch
   * them — see migration 045's header.
   */
  externallyManaged: boolean
  accessStatus: AccessStatus
  /**
   * Where somebody sits, as three flat strings, because the table shows them as
   * three columns (054). They are the SAME rows `orgUnits` carries — read from
   * org_unit_membership, which is what the cascading filter also reads (049), so
   * the column and the filter cannot disagree about a person's placement.
   *
   * `businessUnitName` is what BambooHR calls a DIVISION. One thing, two names,
   * and the schema's name wins on screen: this app has 12 legal entities and 57
   * business units, and introducing a second word for one of them is how a
   * screen and a database start describing different orgs.
   *
   * Null is the honest answer for 21 active staff whose Bamboo row carries no
   * division at all — they are on hr_unmapped as `no_division_mapping`, which is
   * a worklist, not a rendering problem.
   */
  legalEntityName: string | null
  businessUnitName: string | null
  departmentName: string | null
  /** Read-only here. employment is written only by set_employment (049). */
  jobTitle: string | null
  roleKeys: string[]
  roleNames: string[]
  grants: RoleGrant[]
  orgUnits: OrgPlacement[]
}

/**
 * What the table can be ordered by. Every one of these is applied by the
 * DATABASE — see the note on loadRoster. The strings match 054's
 * whitelist exactly, and that function RAISES on anything else rather than
 * falling back to name, so a typo here is a visible error and not a header
 * that silently does nothing.
 */
export type PeopleSort = 'name' | 'business_unit' | 'department' | 'job_title' | 'access' | 'last_sign_in'

type Row = {
  person_id: string
  display_name: string
  locale: string
  is_active: boolean
  membership_active: boolean
  is_verified: boolean | null
  has_signed_in: boolean | null
  last_sign_in_at: string | null
  invited_at: string | null
  last_invite_sent_at: string | null
  externally_managed: boolean
  access_status: AccessStatus
  legal_entity_name: string | null
  business_unit_name: string | null
  department_name: string | null
  job_title: string | null
  role_keys: string[] | null
  role_names: string[] | null
  roles:
    | {
        key: string
        name: string
        scope_type?: 'org_unit' | 'stock_room' | null
        scope_id: string | null
        scope_name: string | null
        scope_active?: boolean
      }[]
    | null
  /** 137: everyone who matched, before the limit. Same on every row. */
  total_count?: number | string | null
  org_units:
    | {
        org_unit_id: string
        name: string
        type: string
        parent_id: string | null
        parent_name: string | null
      }[]
    | null
}

/**
 * Everyone in the tenant, with the roles they hold.
 *
 * LAST SIGN-IN, NEVER LAST ACTIVE (044, David's call on 10 Sep). This file used
 * to say there was no timestamp anywhere near `hasSignedIn`, because a
 * per-person last-seen is the start of the activity tracking rule 8 is about.
 * The objection was to it arriving as a helpful extra column rather than as a
 * decision anybody made; it is now a decision, and 044's header is it.
 *
 * The line that still holds: `auth.users.last_sign_in_at` moves only when
 * somebody actually signs in, and answers "was this invitation taken up, is
 * this account dormant". `auth.sessions.refreshed_at` is ONE JOIN AWAY, moves
 * every time the app refreshes a token, and is a live feed of when each of
 * ~900 people last had the hub open. It looks fresher and it is the wrong
 * column. Two narrowed invariants now fail if a last-ACTIVE column appears.
 *
 * ADMIN ONLY (043, ACC-16). The database refuses this to anyone without
 * admin.roles, and refuses rather than returning an empty list — an empty list
 * would say "there is nobody in this company". Callers should not reach here
 * without `can(me, 'admin.roles')`; the refusal is the gate, that check is the
 * courtesy.
 *
 * `isVerified` and `hasSignedIn` are THREE-valued, and the third value is the
 * whole of migration 042. Until then the RPC joined `identity`, which is
 * self-read-only, so it saw the caller's own row and nobody else's — and
 * reported every colleague as `false`. The screen said "Never signed in"
 * against people who had signed in that week. `null` now means "you may not
 * see this" and must never be collapsed to false on the way to the UI; that
 * collapse IS the bug.
 */
export type RosterFilters = {
  search?: string
  includeSuspended?: boolean
  /**
   * 137: three org filters instead of one. Several values in ONE filter are
   * OR'd; the filters are AND'd with each other, and each matches its unit or
   * anything beneath it. "ConeTec Inc AND Engineering" is the question, and a
   * single combined picker could only ever OR the two.
   */
  legalEntityIds?: string[]
  businessUnitIds?: string[]
  departmentIds?: string[]
  /** Exact match on the current employment record's title. */
  jobTitles?: string[]
  roleKeys?: string[]
  statuses?: AccessStatus[]
  sort?: PeopleSort
  sortDesc?: boolean
  limit?: number
}

/** One page of the roster and how many matched in all. */
export type Roster = { people: PersonRoles[]; total: number }

/**
 * Everyone in the tenant, with the roles they hold (people_roster, 137).
 *
 * SERVER-SIDE, ALL OF IT (039, 044, 046, 054). The limit is applied by the
 * DATABASE, so filtering or sorting the fetched array here would be a different
 * answer that looks like this one. There is deliberately no client-side sort or
 * filter anywhere in this file. `total` is counted before the limit, which is
 * what lets the screen say "200 of 931" instead of presenting a page as the
 * whole company.
 *
 * ADMIN ONLY (043). The database refuses this to anyone without admin.roles —
 * tenant_admin and, since 137, hr_admin.
 */
export async function loadRoster(opts: RosterFilters = {}): Promise<Roster> {
  const { data, error } = await supabase.rpc('people_roster', {
    p_limit: opts.limit ?? 200,
    p_search: opts.search?.trim() || null,
    // Without this a suspended person disappears from the screen that just
    // suspended them, and there is no way to reactivate somebody you cannot
    // find. See migration 027.
    p_include_suspended: opts.includeSuspended ?? true,
    // Empty arrays are sent as-is: null and '{}' mean the same thing to the
    // function, so "cleared" and "never set" cannot become two states.
    p_legal_entities: opts.legalEntityIds ?? [],
    p_business_units: opts.businessUnitIds ?? [],
    p_departments: opts.departmentIds ?? [],
    p_job_titles: opts.jobTitles ?? [],
    p_roles: opts.roleKeys ?? [],
    p_statuses: opts.statuses ?? [],
    p_sort: opts.sort ?? 'name',
    p_sort_desc: opts.sortDesc ?? false,
  })
  if (error) throw error
  const rows = (data ?? []) as Row[]
  return { people: rows.map(rowToPerson), total: rows.length ? Number(rows[0].total_count ?? rows.length) : 0 }
}

/**
 * How many active tenant administrators there are, tenant-wide. Its own small
 * query because counting them from the visible page counts the ones the current
 * FILTER happens to show — filter to Chile and the screen would announce that
 * there are none.
 */
export async function countActiveTenantAdmins(): Promise<number> {
  const { total } = await loadRoster({ roleKeys: ['tenant_admin'], includeSuspended: false, limit: 1 })
  return total
}

/** The job titles the filter offers, with how many people hold each (137). */
export async function loadJobTitles(): Promise<{ title: string; people: number }[]> {
  const { data, error } = await supabase.rpc('people_roster_titles')
  if (error) throw error
  return ((data ?? []) as { job_title: string; people: number | string }[]).map((r) => ({
    title: r.job_title,
    people: Number(r.people),
  }))
}

function rowToPerson(r: Row): PersonRoles {
  return {
    personId: r.person_id,
    displayName: r.display_name,
    locale: r.locale,
    isActive: r.is_active,
    membershipActive: r.membership_active,
    // ?? null, never ?? false — see the header. An absent value means the
    // caller may not see it, and false would state a fact instead.
    isVerified: r.is_verified ?? null,
    hasSignedIn: r.has_signed_in ?? null,
    lastSignInAt: r.last_sign_in_at ?? null,
    invitedAt: r.invited_at ?? null,
    lastInviteSentAt: r.last_invite_sent_at ?? null,
    externallyManaged: r.externally_managed ?? false,
    accessStatus: r.access_status,
    legalEntityName: r.legal_entity_name ?? null,
    businessUnitName: r.business_unit_name ?? null,
    departmentName: r.department_name ?? null,
    jobTitle: r.job_title ?? null,
    orgUnits: (r.org_units ?? []).map((o) => ({
      orgUnitId: o.org_unit_id,
      name: o.name,
      type: o.type,
      parentId: o.parent_id,
      parentName: o.parent_name,
    })),
    roleKeys: r.role_keys ?? [],
    roleNames: r.role_names ?? [],
    grants: (r.roles ?? []).map((g) => ({
      key: g.key,
      name: g.name,
      // 140 sends scope_type; infer it for a row from before, so a scoped role
      // can never be mistaken for a store.
      scopeType: g.scope_type ?? (g.scope_id ? 'org_unit' : null),
      scopeId: g.scope_id,
      scopeName: g.scope_name,
      ...(g.scope_active === undefined ? {} : { scopeActive: g.scope_active }),
    })),
  }
}

/**
 * Grant a role.
 *
 * ── WHY AN RPC AND NOT AN INSERT ────────────────────────────────────────────
 *
 * `role_assignment` has no INSERT policy and must not get one. Three things
 * have to happen together and a policy can only permit one of them: the
 * membership check (a role on a non-member grants permissions inside a tenant
 * that person cannot see), the audit row (ACC-8), and — on revoke — the
 * last-admin count, which a WITH CHECK expression cannot do.
 *
 * The function is SECURITY DEFINER, so the checks inside it are the entire
 * authorisation boundary rather than a second line of defence. That is
 * documented at length in migration 021 and is worth reading before changing
 * anything here.
 *
 * Roles are named by KEY. Sending a role_id from the client would mean sending
 * any uuid, including a role belonging to another tenant.
 */
export async function assignRole(
  personId: string,
  roleKey: string,
  orgUnitId?: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('assign_role', {
    p_person_id: personId,
    p_role_key: roleKey,
    // Omitted: p_tenant_id defaults server-side to the caller's own tenant, and
    // has_permission is then checked against whatever was resolved — so this
    // cannot reach a tenant the caller does not administer.
    p_org_unit_id: orgUnitId ?? null,
  })
  if (error) throw error
}

/**
 * Remove a role. Refused for `employee`, and for the last tenant_admin.
 *
 * The org unit matters: revoke_role matches it with `is not distinct from`, so
 * revoking without a scope removes the TENANT-WIDE grant and leaves any scoped
 * ones standing. That is the correct behaviour and it is easy to misread as a
 * failed revoke, which is why the screen shows each grant separately.
 */
export async function revokeRole(
  personId: string,
  roleKey: string,
  orgUnitId?: string | null,
): Promise<void> {
  const { error } = await supabase.rpc('revoke_role', {
    p_person_id: personId,
    p_role_key: roleKey,
    p_org_unit_id: orgUnitId ?? null,
  })
  if (error) throw error
}

/**
 * 153. Whose list People and roles is. `wide` is admin.roles for the whole
 * company. Otherwise `region` is the caller's grant rows (what the page names)
 * and `unitIds` every active org unit at or under them (what the pickers may
 * offer). Read from my_people_scope(), which uses app.has_permission's own walk
 * — the same answer every people function gives.
 */
export type PeopleScope = {
  wide: boolean
  region: { orgUnitId: string; name: string; type: string }[]
  unitIds: string[]
}

export async function loadPeopleScope(): Promise<PeopleScope> {
  const { data, error } = await supabase.rpc('my_people_scope')
  if (error) throw error
  const j = (data ?? {}) as {
    wide?: boolean
    region?: { org_unit_id: string; name: string; type: string }[]
    units?: string[]
  }
  return {
    wide: j.wide === true,
    region: (j.region ?? []).map((r) => ({ orgUnitId: r.org_unit_id, name: r.name, type: r.type })),
    unitIds: j.units ?? [],
  }
}

/** 153. True when the caller administers a region, not the whole company. */
export function isRegional(scope: PeopleScope | null | undefined): scope is PeopleScope {
  return !!scope && !scope.wide
}

/** 153. "GEOTECH and LANKELMA LIMITED" — the region in words. */
export function regionWords(scope: PeopleScope): string {
  const names = scope.region.map((r) => r.name)
  if (names.length <= 1) return names[0] ?? 'no region'
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * 153. The org units a scoped grant of `role` may name, for this caller: the
 * role's own scope types, and — for a regional HR Administrator — only units
 * at or under their region. The server checks both (assign_role,
 * app.require_grant_in_scope); this only keeps refusals off the picker.
 */
export function scopeOptionsFor<T extends { orgUnitId: string; type: string }>(
  role: Pick<AssignableRole, 'scopeTypes'>,
  orgUnits: T[],
  scope: PeopleScope | null | undefined,
): T[] {
  return orgUnits.filter(
    (o) =>
      (!role.scopeTypes || role.scopeTypes.includes(o.type)) &&
      (!isRegional(scope) || scope.unitIds.includes(o.orgUnitId)),
  )
}
